/* ========================================
   Infinite Scroll Helper
   Used by artist-all-klydos and artist-all-collections to render paginated
   server responses (`{ items, hasMore, page, pageSize, total }`) and load
   the next page when the user nears the bottom of the grid.

   Each page is responsible for its own card markup and click handling — the
   helper only owns pagination state, the IntersectionObserver, and the
   empty-state branch.
   ======================================== */

function createInfiniteScroll(config) {
  const grid = config.grid;
  const sentinel = config.sentinel;
  const pageSize = config.pageSize;
  const fetchPage = config.fetchPage;
  const renderItems = config.renderItems;
  const renderEmpty = config.renderEmpty;
  const onFirstPage = config.onFirstPage;
  const onLoadingChange = config.onLoadingChange;

  const allLoadedItems = [];
  let loadedPagesCount = 0;
  let hasMore = true;
  let isLoading = false;
  let paginationObserver = null;
  /* Bumped by reload() so a fetch that resolves after a reload can tell it has
     been superseded and must not touch the grid or pagination state. */
  let generation = 0;

  function setLoading(nextIsLoading) {
    isLoading = nextIsLoading;
    if (onLoadingChange) onLoadingChange(nextIsLoading);
  }

  function disconnectObserver() {
    if (paginationObserver) {
      paginationObserver.disconnect();
      paginationObserver = null;
    }
  }

  function setupObserver() {
    disconnectObserver();
    paginationObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) loadNextPage();
      });
    }, { rootMargin: '400px 0px' });
    paginationObserver.observe(sentinel);
  }

  /* Clears the grid and repaints it from a freshly fetched page-0 response,
     resetting pagination state. Callers reset/guard state around this. */
  function renderFirstPage(items, response) {
    grid.innerHTML = '';
    allLoadedItems.length = 0;
    loadedPagesCount = 0;
    if (items.length === 0) {
      renderEmpty(grid);
      hasMore = false;
      return;
    }
    for (const item of items) allLoadedItems.push(item);
    renderItems(grid, items, 0);
    loadedPagesCount = 1;
    hasMore = !!(response && response.hasMore);
    if (hasMore) setupObserver();
  }

  async function loadNextPage() {
    if (isLoading || !hasMore) return;
    const requestGeneration = generation;
    setLoading(true);
    try {
      /* Pages are 0-indexed: the first request asks for page 0, then 1, ... */
      const nextPage = loadedPagesCount;
      const response = await fetchPage(nextPage, pageSize);
      if (requestGeneration !== generation) return;
      const items = (response && response.items) || [];

      if (loadedPagesCount === 0) {
        renderFirstPage(items, response);
        if (allLoadedItems.length > 0 && onFirstPage) onFirstPage(allLoadedItems);
        return;
      }

      const startIndex = allLoadedItems.length;
      for (const item of items) allLoadedItems.push(item);
      renderItems(grid, items, startIndex);
      loadedPagesCount += 1;
      hasMore = !!(response && response.hasMore);
      if (!hasMore) disconnectObserver();
    } finally {
      if (requestGeneration === generation) setLoading(false);
    }
  }

  /* Refetch from page 0 with whatever fetchPage now returns (e.g. a new sort).
     The current cards stay on screen until the new first page arrives, then the
     grid is repainted in one step. Returns true if this reload applied; false if
     a newer reload superseded it. A failed reload leaves the existing list
     intact and scrollable. */
  async function reload() {
    const requestGeneration = (generation += 1);
    disconnectObserver();
    setLoading(true);
    try {
      const response = await fetchPage(0, pageSize);
      if (requestGeneration !== generation) return false;
      const items = (response && response.items) || [];
      renderFirstPage(items, response);
      return true;
    } catch (error) {
      if (requestGeneration !== generation) return false;
      if (hasMore && loadedPagesCount > 0) setupObserver();
      throw error;
    } finally {
      if (requestGeneration === generation) setLoading(false);
    }
  }

  return {
    loadNextPage: loadNextPage,
    reload: reload,
    getAllLoaded: function() { return allLoadedItems; },
  };
}
